import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import DialogTitle from '@mui/material/DialogTitle'
import Dialog from '@mui/material/Dialog'
import { CardContent, CircularProgress, FormControl, Grid, IconButton, TextField, Tooltip } from '@mui/material'
import { type QuerySavedResults, querySavedListFilter } from 'services/querySavedListService'
import React, { useEffect, useState } from 'react'
import { MaterialUIPickers, ButtonWithIcon } from 'components'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import dayjs, { type Dayjs } from 'dayjs'

export interface QuerySavedFilters {
  page: number
  moduleId: number
  name?: string
  startDate?: string | null
  endDate?: string | null
}

export interface QuerySaved {
  id: number
  name: string
  query: string
  is_private: boolean
  module: Module
}

export interface Module {
  id: number
  name: string
}

interface QuerySavedFilter {
  name: string
  moduleId: number
  startDate?: string | null
  endDate?: string | null
}

export interface SimpleDialogProps {
  open: boolean
  module: Module
  selectedValue: string
  onClose: (value: string) => void
  queriesSaved: QuerySaved[]
  loading: boolean
  onFilter?: (filters: QuerySavedFilter) => Promise<void>
  clearFilter: () => void
}

const SimpleDialog: React.FC<SimpleDialogProps> = (props: SimpleDialogProps): JSX.Element => {
  const { onClose, selectedValue, open, loading, module } = props

  const [querySavedFilter, setQuerySavedFilter] = useState<QuerySavedFilter>({
    name: '',
    moduleId: module.id
  })

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined
    const minDuration = 1500
    if (loading) {
      timeoutId = setTimeout(() => {
      }, minDuration)
    } else {
      clearTimeout(timeoutId)
    }
    return () => {
      clearTimeout(timeoutId)
    }
  }, [loading, querySavedFilter])

  const handleDateChange = (filterKey: keyof QuerySavedFilter, newValue: Dayjs | null): void => {
    setQuerySavedFilter((prevquerySavedFilter) => ({
      ...prevquerySavedFilter,
      [filterKey]: newValue?.format('YYYY-MM-DD HH:MM:00')
    }))
  }
  const handleChangerSavedFilter = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    filterKey: keyof QuerySavedFilter): void => {
    const value = event.target.value
    setQuerySavedFilter((prevquerySavedFilter) => ({
      ...prevquerySavedFilter,
      [filterKey]: value
    }))
  }

  const handleFilter = (): void => {
    const filter: QuerySavedFilter = {
      name: (querySavedFilter?.name != null) ? querySavedFilter.name : '',
      moduleId: querySavedFilter?.moduleId,
      startDate: querySavedFilter?.startDate,
      endDate: querySavedFilter?.endDate
    }
    void props.onFilter?.(filter)
  }

  const handlerClean = (): void => { props.clearFilter() }

  const handleClose = (): void => {
    onClose(selectedValue)
  }
  function handleListItemClick (value: string): void {
    onClose(value)
  }

  const isValid = (): boolean => {
    if (querySavedFilter?.name === '' || querySavedFilter?.name === undefined) {
      return false
    }
    return true
  }
  const createDate = (date: string | null | null = null): Dayjs | null => {
    if (date != null) {
      return dayjs(date)
    }
    return null
  }

  return (
    <Dialog fullWidth maxWidth="xl" onClose={handleClose} open={open} disableRestoreFocus disableScrollLock>
      <DialogTitle>
        Set your backup query(filter)
        <CardContent>
        <Grid container spacing={1} alignItems="center" justifyContent="center">
          <Grid item xs={3}>
            <FormControl fullWidth variant="outlined">
              <TextField required label="Name" variant="outlined" onChange={(e) => { handleChangerSavedFilter(e, 'name') }} value={querySavedFilter.name}/>
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <MaterialUIPickers value={createDate(querySavedFilter?.startDate)} namelabel='Start date' onChange={(newValue) => { handleDateChange('startDate', newValue) }}></MaterialUIPickers>
          </Grid>
          <Grid item xs={2}>
            <MaterialUIPickers value={createDate(querySavedFilter?.endDate)} namelabel='End date'onChange={(newValue) => { handleDateChange('endDate', newValue) }}></MaterialUIPickers>
          </Grid>
          <Grid item xs={2} style={{ marginLeft: '5%', marginRight: '-4%' }} container alignItems="center" justifyContent="center">
            <ButtonWithIcon disabled={!isValid()} size='large' nameButton='search' icon={<FilterAltIcon/>} onClick={handleFilter}></ButtonWithIcon>
          </Grid>
          <Grid item xs={1.5} container alignItems="center" justifyContent="center">
            <ButtonWithIcon variant='outlined' nameButton='Clean' icon={<CleaningServicesIcon/>} onClick={handlerClean}></ButtonWithIcon>
          </Grid>
        </Grid>
      </CardContent>

      <hr style={{ background: 'black', color: 'gray', borderColor: 'gray', height: '1px' }} />

      </DialogTitle>
      {loading && <CircularProgress sx={{ alignSelf: 'center', mb: 5 }}/>}
      {!loading && <List sx={{ pt: 0 }}>
        {props.queriesSaved.map((querySaved) => (
          <ListItem key={querySaved.id} disableGutters>
            <ListItemButton onClick={() => { handleListItemClick(querySaved.query) } } key={querySaved.id}>
              <ListItemText primary={querySaved.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>}
    </Dialog>
  )
}

export interface SimpleDialogDemoFiltersProps {
  onClose: (value: string) => void
  module: Module
}

export const SimpleDialogQuery: React.FC<SimpleDialogDemoFiltersProps> = (props: SimpleDialogDemoFiltersProps): JSX.Element => {
  const [open, setOpen] = React.useState(false)
  const [selectedValue, setSelectedValue] = React.useState('')
  const [loading, setLoading] = useState<boolean>(false)
  const [queriesSaved, setQueriesSaved] = useState<QuerySaved[]>([])
  const [filters, setFilters] = useState<QuerySavedFilters>({
    page: 1,
    moduleId: props.module.id
  })
  const { onClose, module } = props

  function handleClickOpen (): void {
    const updateTablePage = async (): Promise<void> => {
      await updateQuerySavedWithFilter()
    }
    void updateTablePage()
    setOpen(true)
  }

  const handleClose = (value: string): void => {
    setOpen(false)
    setSelectedValue(value)
    onClose(value)
  }
  const fetchQuerySavedList = async (): Promise<QuerySavedResults> => {
    setLoading(true)
    try {
      const params: any = {}
      if (filters.page !== 0) {
        params.page = filters.page
        params.moduleId = module.id
      }
      return await querySavedListFilter(params)
    } catch (error) {
      console.error(error)
      return { data: [], page_number: 1, page_size: 1, total_pages: 1, total_items: 0 }
    }
  }

  const updateQuerySavedWithFilter = async (): Promise<void> => {
    fetchQuerySavedList().then(response => {
      setQueriesSaved(response.data)
      if (filters.page !== response.page_number) {
        setFilters({ page: response.page_number, moduleId: 0 })
      }
    }).catch(reason => {
      console.log(reason)
      setQueriesSaved([])
    }).finally(() => {
      setLoading(false)
    })
  }
  const filterQuerySaved = async (filter: QuerySavedFilter): Promise<void> => {
    setLoading(true)
    const params: QuerySavedFilters = {
      page: 0,
      moduleId: filter.moduleId,
      name: filter.name,
      startDate: filter.startDate,
      endDate: filter.endDate
    }
    querySavedListFilter(params).then(result => {
      setQueriesSaved(result.data)
    })
      .catch(() => {
        setQueriesSaved([])
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const clearFilters = (): void => { setFilters({ page: 0, moduleId: 0 }) }

  return (
    <div>
      <Tooltip title={'Load A Saved Search'} >
        <span>
          <IconButton size='large' onClick={handleClickOpen} >
            <FormatListBulletedIcon color={'primary'}/>
          </IconButton>
        </span>
      </Tooltip>
     <SimpleDialog
        module={module}
        selectedValue={selectedValue}
        open={open}
        onClose={handleClose}
        queriesSaved={queriesSaved}
        loading={loading}
        onFilter={filterQuerySaved}
        clearFilter={clearFilters}
      />
    </div>
  )
}
